import dayjs from 'dayjs';
import {action, computed, makeObservable, observable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {TextInput} from '../../../../../uikit/TextInput';
import type {GetComponentProps} from '../../../../../utils/utility-types';
import type {CRUEventConfig, IScheduleItemRepeat} from '../types';

import {
  EVERGREEN_PROGRAM_SCHEDULE_DURATION,
  isEditionEvergreen,
} from '../../../../../utils/entity/program';
import {eventPayloadActionCreator} from '../../../../../utils/events';
import {getUuid} from '../../../../../utils/getUuid';
import {
  getDescriptionSchema,
  getTitleSchema,
  validateFields,
} from '../../../../../utils/validation';
import {createTextInputField} from '../../../../../utils/validation/createTextInputField';
import {ScheduleItemRepeat} from '../utils';
import {REPEATS} from './utils';

export class CRUProgramScheduleEventStore {
  date = new Date();
  day = 0;
  fields = {
    day: createTextInputField('day', {
      customValidate: action(value => {
        const field = this.fields.day;

        let isValid = true;

        field.error = null;

        if (!value) {
          field.error = i18n.t(
            'shared:CRUProgramScheduleEvent.day_required_error_message',
          );
          isValid = false;
        } else if (this.day > this.programDuration - 1) {
          field.error = i18n.t(
            'shared:CRUProgramScheduleEvent.day_max_error_message',
            {
              value: this.programDuration,
            },
          );
          isValid = false;
        }

        field.isValid = isValid;

        return isValid;
      }),
      validationRule: {
        type: 'string',
      },
    }),
    description: createTextInputField('description', {
      validationRule: getDescriptionSchema(),
    }),
    stopDay: createTextInputField('stopDay', {}),
    title: createTextInputField('title', {
      validationRule: getTitleSchema(),
    }),
  };
  groupUuid = getUuid();
  initialRepeat = ScheduleItemRepeat.ONE_TIME!;
  isEdit = false;
  program?: CRUEventConfig['program'];
  repeat = ScheduleItemRepeat.ONE_TIME!;
  uuid = getUuid();

  constructor({program}: CRUEventConfig) {
    this.program = program;

    makeObservable(this, {
      availableRepeats: computed,
      date: observable,
      day: observable,
      initialRepeat: observable,
      isEdit: observable,
      isFormValid: computed,
      isRecurring: computed,
      lastEdition: computed,
      onDayChange: action.bound,
      onTimeChange: action.bound,
      programDuration: computed,
      recurringDisclaimerTitle: computed,
      repeat: observable,
      resetRepeat: action.bound,
      saveButtonLabel: computed,
      setDate: action.bound,
      setDay: action.bound,
      setRepeat: action.bound,
      shouldShowRecurringDisclaimer: computed,
    });
  }

  get availableRepeatIds() {
    return this.availableRepeats.map(repeat => repeat.id);
  }

  get availableRepeats() {
    return REPEATS.filter(repeat => this.programDuration > repeat.interval);
  }

  get dayIsAvailable() {
    return true;
  }

  get isFormValid() {
    return validateFields(this.fields);
  }

  get isRecurring() {
    return this.repeat.id !== ScheduleItemRepeat.ONE_TIME!.id;
  }

  get lastEdition() {
    return this.program?.editions.slice().pop();
  }

  get programDuration() {
    return isEditionEvergreen(this.lastEdition)
      ? EVERGREEN_PROGRAM_SCHEDULE_DURATION
      : this.lastEdition?.duration || 1;
  }

  get recurringDisclaimerTitle() {
    return '';
  }

  get saveButtonLabel() {
    return this.isEdit ? i18n.t('shared:update') : i18n.t('shared:add');
  }

  get shouldShowRecurringDisclaimer() {
    return false;
  }

  isRepeatAvailable(repeat: IScheduleItemRepeat) {
    return this.availableRepeatIds.includes(repeat.id);
  }

  onDayChange(
    e: Parameters<GetComponentProps<typeof TextInput>['onChange']>[0],
  ) {
    let dayText = e.payload?.text.replace(/\D/g, '') || '';

    if (dayText === '0') {
      dayText = '';
    }

    this.day = dayText ? parseInt(dayText, 10) : 0;

    if (this.day > 0) {
      this.setDay(this.day - 1);
    }

    this.fields.day.onChange?.(
      eventPayloadActionCreator({text: dayText}, e.event),
    );
  }

  onTimeChange(
    e: Parameters<GetComponentProps<typeof TextInput>['onChange']>[0],
  ) {
    const timeString = e.payload?.text;

    if (timeString) {
      const [hours, mins] = timeString.split(':');

      this.setDate(
        dayjs(this.date)
          .startOf('day')
          .add(parseInt(hours || '0', 10) || 0, 'hours')
          .add(parseInt(mins || '0', 10) || 0, 'minutes')
          .toDate(),
      );
    }
  }

  resetRepeat() {
    this.repeat = ScheduleItemRepeat.ONE_TIME!;
  }

  setDate(date: Date) {
    this.date = date;
  }

  setDay(day: number) {
    this.day = day;
  }

  setRepeat(repeat: IScheduleItemRepeat) {
    this.repeat = repeat;
  }
}
